<template>
  <v-dialog
    class="ad-body"
    v-model="dialog"
    :scrim="false"
    max-width="768px"
    @click:outside="closeModal"
    persistent
  >
    <div class="relative w-full bg-grey-lighten-1 overflow-y-scroll">
      <div class="p-4 mx-auto h-full w-full">
        <div class="flex justify-between items-start mb-4">
          <div>
            <div class="text-h6 text-grey-darken-4 mb-2">
              {{ name }}
            </div>
            <div class="text-body-1 text-grey-darken-4 d-flex align-center flex-wrap gap-2">
              <span class="text-grey-darken-2">Practitioner:</span>
              <v-select
                  v-model="doctorOfTreatment"
                  :items="relatedDoctorsToTreatment"
                  id="doctorSelect"
                  item-title="name"
                  item-value="uuid"
                  return-object
                  variant="underlined"
                  density="compact"
                  hide-details
                  :menu-props="{
                    maxHeight: 'auto'
                  }"
                  class="doctor-select"
              >
                <template #selection="{ item }">
                  <div
                      class="text-xs font-bold text-center d-inline pa-1 rounded"
                      :style="{
                        backgroundColor: item.raw.color,
                        color: $filters.foregroundColor(item.raw.color),
                        marginRight: '10px !important',
                      }"
                  >
                    {{ $filters.initials(item.raw.name) }}
                  </div>
                  <b>{{ item.raw.name }}</b>
                </template>
                <template #item="{ item, props }">
                  <v-list-item v-bind="props" :title="undefined">
                    <div class="d-flex align-center">
                      <div
                          class="text-xs font-bold text-center d-inline pa-1 rounded"
                          :style="{
                            backgroundColor: item.raw.color,
                            color: $filters.foregroundColor(item.raw.color),
                            marginRight: '10px !important',
                          }"
                      >
                        {{ $filters.initials(item.raw.name) }}
                      </div>
                      <b>{{ item.raw.name }}</b>
                    </div>
                  </v-list-item>
                </template>
              </v-select>
              <span class="text-grey-darken-2">{{ roomName }}</span>
            </div>
          </div>
          <v-btn
            color="grey-darken-1"
            icon="mdi-close"
            density="comfortable"
            variant="text"
            @click="closeModal"
          />
        </div>
        <v-divider></v-divider>
        <div class="flex flex-col items-center pt-4" style="height: 650px; overflow-y: scroll;">
          <div class="text-h6 text-grey-darken-2 mb-2">
            Time Spent With Patient:
          </div>
          <div class="text-h4 font-weight-medium text-grey-darken-4 mb-6">
            {{ timeFormatted }}
          </div>
          <div class="text-h6 text-grey-darken-2 mb-2">
            Allocated Time
          </div>
          <div class="flex items-center mb-6">
            <TextCard
              class="mr-4"
              small
              text="-1 min"
              @click="$emit('update-allocated-time', -60)"
            />
            <div class="text-h4 font-weight-medium text-grey-darken-4">
              {{ allocatedTimeFormatted }}{{ allocatedOverHour ? 'm' : 's'}}
            </div>
            <TextCard
              class="ml-4"
              small
              text="+1 min"
              @click="$emit('update-allocated-time', 60)"
            />
          </div>
          <v-btn
            class="text-black mb-6 "
            border="blue-300"
            :disabled="
              cardState === 'completed' || (
                appointment && appointment.treatments
                  .filter(({ uuid }) => uuid !== this.treatment.uuid)
                  .some(({ started_at: a, ended_at: b }) => (!!a && !b))
              ) || isDisabled
            "
            :icon="stateIcon ? 'mdi-play' : 'mdi-pause'"
            variant="outlined"
            size="large"
            @click="togglePlayState($event)"
          />
          <div class="w-full flex justify-center px-2">
            <v-btn
              class="mr-6"
              color="primary"
              :disabled="(cardState === 'completed') || !treatment.started_at || isDisabled"
              @click="handleCompleteTreatment"
            >
              COMPLETE TREATMENT
            </v-btn>
            <v-btn
              class="ml-6"
              color="grey-darken-1"
              variant="tonal"
              :disabled="cardState === 'default' && !treatment.started_at || isDisabled"
              @click="$emit('reset-treatment')"
            >
              RESET
            </v-btn>
          </div>

          <div class="mt-6 mb-3 px-2 w-full">
              <div>
                  <p class="flag-title mb-2">TYPES AND FLAGS</p>
              </div>
              <div>
                  <v-chip
                      v-for="(flag, index) in currentOpenFlags"
                      :key="`treatment-flags-${index}`"
                      class="mr-2 mb-2 flags"
                  >
                      <v-icon
                              v-if="appointment.has_flags"
                              color="red-darken-2"
                              :size="18">mdi-flag</v-icon>
                      {{ `${flag.message}${flag.description ? `: ${flag.description}` : ''}` }}
                  </v-chip>
                  <v-chip
                      v-for="(tFlag, index) in currentOpenTractionFlags"
                      :key="`treatment-t-flags-${index}`"
                      class="mr-2 mb-2 tFlags"
                  >
                      <v-icon
                          color="green-darken-2"
                          :size="18">mdi-flag</v-icon>
                      {{ `${tFlag.message}${tFlag.description ? `: ${tFlag.description}` : ''}` }}
                  </v-chip>
                  <v-chip
                          v-for="(profile, index) in currentOpenProfiles"
                          :key="`treatment-profiles-${index}`"
                          class="mr-2 mb-2 types"
                  >
                      {{ profile.name }}
                  </v-chip>
              </div>
          </div>

          <div class="mt-6 mb-3 px-2 w-full">
              <div>
                  <p class="flag-title mb-2">NOTES</p>
              </div>
              <div>
                  <TreatmentNotesCard
                          :notes="currentGlobalNotes"
                          :appt-treatment-id="treatment.uuid"
                          :appointment-id="appointment.uuid"
                          :patient-id="appointment.patient.uuid"
                          :patient-name="appointment.patient.name"
                          :treatment-name="`Global`"
                          hide-patient
                          @refresh-notes="refreshAppointment"
                          @set-allowed-close="setAllowedToClose"
                  />
                  <TreatmentNotesCard
                          hide-patient
                          :notes="currentLocalNotes"
                          :appt-treatment-id="treatment.uuid"
                          :treatment-id="treatment.treatment.uuid"
                          :appointment-id="appointment.uuid"
                          :patient-id="appointment.patient.uuid"
                          :patient-name="appointment.patient.name"
                          :treatment-name="treatment.treatment.name"
                          @refresh-notes="refreshAppointment"
                          @set-allowed-close="setAllowedToClose"
                  />
              </div>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import TextCard from '@/components/cards/TextCard.vue';
import TreatmentNotesCard from '@/components/cards/TreatmentNotesCard.vue';
import {mdiPause, mdiPlay} from '@mdi/js';
import { defineComponent } from 'vue';
import {mapActions, mapGetters, mapState} from 'vuex';

export default defineComponent({
  name: 'TreatmentDialog',
  components: { TextCard, TreatmentNotesCard },
  emits: ['close', 'play', 'pause', 'complete-treatment', 'reset-treatment', 'update-allocated-time'],
  props: {
    name: String,
    administrator: { type: String, required: true},
    roomName: String,
    allocatedTime: Number,
    time: Number,
    cardState: String,
    notes: Array,
    appointment: { type: Object, required: true },
    appointmentId: String,
    treatment: { type: Object, required: true },
    isDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: true,
      pauseIcon: mdiPause,
      playIcon: mdiPlay,
      overHour: false,
      allocatedOverHour: false,
      doctorOfTreatment: null,
      allowedToClose: true,
      count: 0,
    };
  },
  created() {
    this.doctorOfTreatment = this.doctors.find( (k) => k.uuid === this.administrator );

    const notesPayload = {
      appointment_treatment_uuid: this.treatment.uuid,
    };
    this.$store.dispatch('LOAD_NOTES_BY_TREATMENT', {params: notesPayload});

    if (this.dialog) {
      this.count = 0;
      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.count++;
      }, 30);
    }
  },
  unmounted() {
    this.$store.commit('setNotesForTreatmentDialog', {notes: []});
    this.$store.commit('setFlagsForDisplay', {notes: []});
    this.$store.commit('setTractionFlagsForDisplay', {notes: []});
  },
  watch: {
    doctorOfTreatment: 'updateDoctor',
  },
  computed: {
    ...mapGetters(['notesForCurrentOpenTreatment', 'currentOpenFlags', 'currentOpenTractionFlags', 'currentOpenProfiles']),
    ...mapState({
      // @ts-ignore
      doctors: (state) => state.doctors,
    }),
    currentGlobalNotes() {
      return this.notesForCurrentOpenTreatment.filter((note) => !note.treatment && !note.treatment_id);
    },
    currentLocalNotes() {
      return this.notesForCurrentOpenTreatment.filter((note) => !!note.treatment || !!note.treatment_id);
    },
    relatedDoctorsToTreatment() {
      const currentTreatmentUUID = this.treatment.treatment.uuid;

      return this.doctors.filter((doctor) => {
        return doctor.treatments.some(({uuid}) => uuid === currentTreatmentUUID) && doctor.is_enabled === 'yes';
      });
    },
    stateIcon(): boolean {
      const playStates = ['default', 'completed'];
      return playStates.includes(this.cardState)
    },
    timeInTreatment() {
      const {
        started_at: timeStarted, ended_at: timeEnded,
        resumed_at: timeResumedAt, status, time_elapsed: timeElapsedBeforeSeconds,
      } = this.treatment;

      const isPaused = status === 'paused';
      const timeElapsedBefore = timeElapsedBeforeSeconds * 1000;
      const timeResumed =
          (!!timeStarted && !isPaused && !!timeResumedAt)
              ? this.$dayjs.utc(timeResumedAt).valueOf()
              : 0;

      const timeSinceResume =
          (!!timeStarted && !timeEnded && !isPaused)
              ? this.time.valueOf() - timeResumed
              : 0;

      return timeElapsedBefore + timeSinceResume;
    },
    timeFormatted(): any {
      return this.$filters.timestampString(this.time);
    },
    allocatedTimeFormatted(): any {
      // MULTIPLY BY 60
      const dur = this.$dayjs.duration(this.allocatedTime).asMilliseconds();
      const format = dur >= 3600000 ? 'hh:mm' : 'mm:ss';
      this.allocatedOverHour = format === 'hh:mm';
      return this.$dayjs.utc(dur).format(format);
    },
  },
  methods: {
    ...mapActions([
      'UPDATE_DOCTOR_OF_APPOINTMENT_TREATMENT',
    ]),
    closeModal() {
      if (this.count > 20 && this.allowedToClose) {
        this.$emit('close');
      }
    },
    setAllowedToClose(value: boolean) {
      this.allowedToClose = value;
    },
    togglePlayState(event: any): void {
      event.target.blur();
      if (this.cardState === 'default') {
        this.$emit('play');
      } else {
        this.$emit('pause');
      }
    },
    handleCompleteTreatment() {
      this.$emit('complete-treatment');
      this.$emit('close');
    },
    async refreshAppointment() {
      const notesPayload = {
        appointment_treatment_uuid: this.treatment.uuid,
      };
      await this.$store.dispatch('LOAD_NOTES_BY_TREATMENT', {params: notesPayload});
    },
    async updateDoctor() {

      if (this.doctorOfTreatment.uuid !== this.administrator) {
        const result = await this.UPDATE_DOCTOR_OF_APPOINTMENT_TREATMENT({ appointmentId: this.appointmentId, params: {
            treatment: this.treatment.uuid,
            doctor: this.doctorOfTreatment.uuid,
          }});

        if (!result) {
          this.doctorOfTreatment = this.administrator;
        }
      }

    },
  },
});
</script>

<style scoped>
.ad-body {
  min-height: 512px;
  max-height: 100%;
  background: #00000030;
}
.flag-title {
    color: #000000;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
}

.flags {
    background-color: #FFDEDE !important;
    color: #AB0000;
    font-weight: 500;
}

.tFlags {
    background-color: #b5f7d3 !important;
    color: #00b050;
    font-weight: 500;
}

.types {
    background-color: #EBE7E7 !important;
    color: #000;
    font-weight: 500;
}

.doctor-select {
  width: max-content;
  max-width: 350px;
  display: inline-flex;
  vertical-align: baseline;
  padding-top: 0;
}

.doctor-select :deep(.v-field__input) {
    padding: 0 0 4px 0;
    min-height: auto;
    align-items: center;
}

.doctor-select :deep(.v-field) {
    background: transparent;
    align-items: center;
    padding-bottom: 0;
}

.doctor-select :deep(.v-field__append-inner) {
    padding-top: 0;
    padding-bottom: 4px;
    align-items: center;
}

.doctor-select :deep(.v-input__control) {
    min-height: auto;
}

.doctor-select :deep(.v-field__outline) {
    --v-field-border-opacity: 0.3;
}
</style>
